import React, { FC, useCallback, useMemo } from 'react';
import { DateRange } from 'react-day-picker';
import { useFormContext, useWatch } from 'react-hook-form';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import classNames from '@/lib/classNames';
import { format } from 'date-fns';
import { Calendar } from '../../elements/Calendar';
import { FormControl, FormField, FormItem, FormLabel } from '../../elements/Form';
import { Popover, PopoverContent, PopoverTrigger } from '../../elements/Popover';
import { Icon } from '../../global/Icons';

interface Props {
    name: string;
    label?: string;
    ariaLabel?: string;
    hasError: boolean;
}

const DateRangePicker: FC<React.HTMLAttributes<HTMLDivElement> & Props> = ({ name, ariaLabel, hasError, label }) => {
    const { control, setValue } = useFormContext();
    const isDesktop = useMediaQuery('lg');

    // From date is tomorrow
    const fromDate = new Date();
    fromDate.setDate(fromDate.getDate() + 1);
    fromDate.setHours(0, 0, 0, 0);

    const [pickUpDate, dropOffDate] = useWatch({
        name: ['pickUpDate', 'dropOffDate'],
    });
    const range = useMemo(() => {
        return { from: pickUpDate, to: dropOffDate };
    }, [pickUpDate, dropOffDate]);

    const handleOnChange = useCallback(
        (newRange: DateRange | undefined) => {
            const fixDateBeingOneOff = (date: Date | undefined) => {
                if (!date) {
                    return date;
                }
                const currentMonthNum = date.getMonth() + 1;
                const currentMonthStr = (currentMonthNum < 10 ? '0' : '') + currentMonthNum;
                const currentDayNum = date.getDate();
                const currentDayStr = (currentDayNum < 10 ? '0' : '') + currentDayNum;
                const dateTimeString = `${date.getFullYear()}-${currentMonthStr}-${currentDayStr}`;

                return new Date(dateTimeString);
            };

            setValue('pickUpDate', fixDateBeingOneOff(newRange?.from), {
                shouldDirty: !!newRange?.from,
            });

            setValue('dropOffDate', fixDateBeingOneOff(newRange?.to), {
                shouldDirty: !!newRange?.to,
            });
        },
        [setValue]
    );

    return (
        <FormField
            control={control}
            name={name}
            render={({ field }) => (
                <FormItem className="relative h-full w-full min-h-[50px] min-w-[100px] flex items-center">
                    <FormLabel className="text-[0.5rem] 2xl:text-[0.75rem] uppercase" float={true} activateFloat={field.value}>
                        {label ?? ariaLabel}
                    </FormLabel>
                    <Popover>
                        <PopoverTrigger asChild>
                            <FormControl>
                                <button
                                    id="date"
                                    className={classNames(
                                        'w-full h-full text-center inline-flex items-center gap-2 justify-between rounded-xl font-md leading-120 font-normal whitespace-nowrap transition duration-300 p-0 lg:text-[0.95vw] xl:text-sm 2xl:text-base text-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 hover:bg-transparent hover:text-primary',
                                        hasError && 'text-destructive border border-destructive p-1 hover:text-destructive'
                                    )}
                                >
                                    <span>{field.value ? format(field.value, 'd LLL y') : ariaLabel}</span>
                                    <Icon id="chevron-down" className="h-5 w-5" aria-label="" />
                                </button>
                            </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                            <Calendar monthToShow={pickUpDate} mode="range" selected={range} onSelect={handleOnChange} numberOfMonths={isDesktop ? 2 : 1} disabled={{ before: fromDate }} />
                        </PopoverContent>
                    </Popover>
                </FormItem>
            )}
        />
    );
};

export default DateRangePicker;
